/**
 * Adjudication rules (#144, approve by exception). Pure, no AWS imports:
 * used by the admin screens (@shared/adjudication) and by adjudicate-catch's
 * publish guard, so the checklist the admin sees and the server's refusal are
 * the same function.
 *
 * Catches stay auto-approved (see the 19 Sep rationale in submit-catch). A
 * person must Approve or Disqualify a catch before Publish if it is PLACED
 * (top 6 of its category on the leaderboard) or FLAGGED.
 */
import { buildCategoryRankings, competitorKey, PLACED_RANK_LIMIT, rankByCatchId, isRankableLength } from './leaderboardRanking';
import { isMysteryFishMatch } from './competitionRules';

export { PLACED_RANK_LIMIT } from './leaderboardRanking';

/**
 * AI suspicion score at or above which a catch is flagged. Chosen from the 43
 * September 2026 catches (read-only, 15 Oct 2026): scores were 10 (37 catches),
 * 30 (3), 35 (2) and 65 (1); every catch with a real flag (LENGTH_INCONSISTENT,
 * NO_EVENT_STICKER, NO_FISH_ON_MAT) scored 30 or more, and every catch with only
 * GPS_NOT_AVAILABLE scored 10. 30 is the lowest score a real flag produced.
 */
export const AI_FLAG_SCORE_THRESHOLD = 30;

/** Flags that say nothing about the catch itself (every September catch had it). */
const IGNORED_FLAGS = new Set(['GPS_NOT_AVAILABLE']);

export type CatchStatus = 'PENDING' | 'APPROVED' | 'DISQUALIFIED';

export interface ReviewCatch {
  id: string;
  status?: string | null;
  species: string;
  lengthCm: number;
  lengthUnusual?: boolean | null;
  aiSuspicionScore?: number | null;
  aiFlags?: unknown;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  memberSub?: string | null;
  memberId?: string | null;
  participantEmail?: string | null;
  competitorName?: string | null;
}

/** aiFlags arrives as an array, a JSON string, or a double-encoded JSON string. Returns the flag codes. */
export function flagCodes(aiFlags: unknown): string[] {
  let v: unknown = aiFlags;
  for (let i = 0; i < 2 && typeof v === 'string'; i++) {
    try { v = JSON.parse(v); } catch { return []; }
  }
  if (!Array.isArray(v)) return [];
  return v
    .map((f) => (typeof f === 'string' ? f : (f as { code?: unknown })?.code))
    .filter((c): c is string => typeof c === 'string' && c.length > 0);
}

const FLAG_SENTENCES: Record<string, string> = {
  LENGTH_INCONSISTENT: 'The length entered does not look like the length shown on the measuring mat.',
  NO_EVENT_STICKER: "The competition sticker can't be seen in the photo.",
  NO_FISH_ON_MAT: "The fish doesn't appear to be on the measuring mat.",
  SPECIES_MISMATCH: "The fish in the photo doesn't look like the species entered.",
  GPS_OUTSIDE_ZONE: 'The photo location is outside the competition area.',
  GPS_NOT_AVAILABLE: 'The photo has no location data (common, not a concern on its own).',
  AI_GENERATED_IMAGE: 'The photo may have been generated or edited by software.',
};

/** A plain-English sentence for an AI flag code; unknown codes get a readable fallback. */
export function describeFlag(code: string): string {
  if (FLAG_SENTENCES[code]) return FLAG_SENTENCES[code];
  const words = String(code || 'unknown').toLowerCase().replace(/_/g, ' ');
  return `The AI check raised: ${words}.`;
}

export function isFlagged(c: ReviewCatch): boolean {
  if (c.lengthUnusual === true) return true;
  if (typeof c.aiSuspicionScore === 'number' && c.aiSuspicionScore >= AI_FLAG_SCORE_THRESHOLD) return true;
  return flagCodes(c.aiFlags).some((code) => !IGNORED_FLAGS.has(code));
}

/**
 * "Checked by ..." text. adjudicate-catch records the admin's email when the
 * token carries it, otherwise their Cognito id (a UUID); an id is shown as "an admin".
 */
export function checkedByLabel(c: ReviewCatch, formatTime: (iso: string) => string): string {
  if (!c.reviewedBy) return 'Not checked';
  const isCognitoId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(c.reviewedBy);
  const who = isCognitoId ? 'an admin' : c.reviewedBy;
  return `Checked by ${who}${c.reviewedAt ? `, ${formatTime(c.reviewedAt)}` : ''}`;
}

export function isReviewed(c: ReviewCatch): boolean {
  return !!c.reviewedBy;
}

export function isDisqualified(c: ReviewCatch): boolean {
  return c.status === 'DISQUALIFIED';
}

/**
 * Rank of every placed-candidate catch, from the leaderboard's own ranking
 * (APPROVED catches only, best per competitor), plus the mystery fish ranking
 * (the shared match rule, best per competitor). The best rank
 * wins when a catch appears in both.
 */
export interface MysteryFish {
  mysteryFishSpecies?: string | null;
  mysteryFishSize?: number | null;
}

/**
 * The mystery fish catches, exactly as calculate-leaderboard picks them: the
 * shared isMysteryFish rule applied when results are read (#134 D3;
 * nothing is stamped on catches any more). A3 (#165): a non-rankable length
 * is excluded here too, same as the category rankings and champion points —
 * an over-cap or malformed length can never win the mystery fish prize.
 */
export function mysteryCandidates(approved: ReviewCatch[], mystery?: MysteryFish | null): ReviewCatch[] {
  if (!mystery?.mysteryFishSpecies) return [];
  return approved
    .filter((c) => isRankableLength(c.lengthCm))
    .filter((c) => isMysteryFishMatch(c.species, c.lengthCm, mystery.mysteryFishSpecies, mystery.mysteryFishSize));
}

export function computeRanks(catches: ReviewCatch[], mystery?: MysteryFish | null): Record<string, number> {
  const approved = catches.filter((c) => c.status === 'APPROVED');
  const ranks = rankByCatchId(buildCategoryRankings(approved as any));
  const best: Record<string, ReviewCatch> = {};
  for (const c of mysteryCandidates(approved, mystery)) {
    const k = competitorKey(c);
    if (!best[k] || c.lengthCm > best[k].lengthCm) best[k] = c;
  }
  Object.values(best)
    .sort((a, b) => b.lengthCm - a.lengthCm)
    .forEach((c, i) => {
      const r = i + 1;
      if (ranks[c.id] === undefined || r < ranks[c.id]) ranks[c.id] = r;
    });
  return ranks;
}

export function isPlaced(rank: number | null | undefined): boolean {
  return typeof rank === 'number' && rank >= 1 && rank <= PLACED_RANK_LIMIT;
}

export interface NeedsReviewItem {
  catchItem: ReviewCatch;
  rank: number | null;
  placed: boolean;
  flagged: boolean;
  reasons: string[];
}

/** Placed or flagged, not checked by a person, not disqualified. */
export function needsReview(c: ReviewCatch, rank: number | null | undefined): boolean {
  if (isDisqualified(c) || isReviewed(c)) return false;
  return isPlaced(rank) || isFlagged(c);
}

/** The Needs review list, most important first: placed catches by rank, then flagged ones. */
export function selectNeedsReview(catches: ReviewCatch[], ranks: Record<string, number> = computeRanks(catches)): NeedsReviewItem[] {
  const items: NeedsReviewItem[] = [];
  for (const c of catches) {
    const rank = ranks[c.id] ?? null;
    if (!needsReview(c, rank)) continue;
    const placed = isPlaced(rank);
    const flagged = isFlagged(c);
    const reasons: string[] = [];
    if (placed) reasons.push(`Placed ${ordinal(rank as number)} in its category`);
    if (c.lengthUnusual) reasons.push('Unusual length');
    if (typeof c.aiSuspicionScore === 'number' && c.aiSuspicionScore >= AI_FLAG_SCORE_THRESHOLD) reasons.push(`AI score ${c.aiSuspicionScore}`);
    for (const code of flagCodes(c.aiFlags)) if (!IGNORED_FLAGS.has(code)) reasons.push(describeFlag(code));
    items.push({ catchItem: c, rank, placed, flagged, reasons });
  }
  return items.sort((a, b) => {
    if (a.placed !== b.placed) return a.placed ? -1 : 1;
    if (a.placed && b.placed) return (a.rank as number) - (b.rank as number);
    return (b.catchItem.aiSuspicionScore || 0) - (a.catchItem.aiSuspicionScore || 0);
  });
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

/**
 * One place that decides "adjudicated" (#134: swapped from the stored
 * stored competition state enum to the dated adjudicatedAt field, the one human
 * decision deriveLifecycle also reads).
 */
export function isAdjudicated(event: { adjudicatedAt?: string | null }): boolean {
  return !!event.adjudicatedAt;
}

export interface ChampionStatus {
  /** A champion candidate exists in this division (it has standings). */
  seniorRequired: boolean;
  juniorRequired: boolean;
  seniorConfirmed: boolean;
  juniorConfirmed: boolean;
}

export interface PublishReadiness {
  ready: boolean;
  blockers: string[];
  warnings: string[];
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * Whether a competition can be published, as sentences that name the count
 * and the action. No bypass (D4): the admin resolves each blocker.
 */
export function getPublishReadiness(input: {
  event: { adjudicatedAt?: string | null } & MysteryFish;
  catches: ReviewCatch[];
  champions: ChampionStatus;
}): PublishReadiness {
  const { event, catches, champions } = input;
  const blockers: string[] = [];
  const warnings: string[] = [];

  if (!isAdjudicated(event)) {
    blockers.push('The competition has not been marked adjudicated. Use Mark Adjudicated first.');
  }

  const ranks = computeRanks(catches, event);
  const pending = selectNeedsReview(catches, ranks);
  const placed = pending.filter((i) => i.placed);
  const flaggedOnly = pending.filter((i) => !i.placed);
  const unusual = placed.filter((i) => i.catchItem.lengthUnusual === true);

  if (placed.length) {
    blockers.push(`${plural(placed.length, 'placed catch has', 'placed catches have')} not been checked by a person. Approve or disqualify ${placed.length === 1 ? 'it' : 'them'} in Needs review.`);
  }
  if (flaggedOnly.length) {
    blockers.push(`${plural(flaggedOnly.length, 'flagged catch has', 'flagged catches have')} not been checked by a person. Approve or disqualify ${flaggedOnly.length === 1 ? 'it' : 'them'} in Needs review.`);
  }
  if (unusual.length) {
    blockers.push(`${plural(unusual.length, 'placed catch has', 'placed catches have')} an unusual length and ${unusual.length === 1 ? 'has' : 'have'} not been checked. Confirm the length by approving, or disqualify.`);
  }
  if (champions.seniorRequired && !champions.seniorConfirmed) {
    blockers.push('The senior champion has not been confirmed. Confirm it on the Champion Points tab.');
  }
  if (champions.juniorRequired && !champions.juniorConfirmed) {
    blockers.push('The junior champion has not been confirmed. Confirm it on the Champion Points tab.');
  }

  if (catches.length === 0) {
    warnings.push('There are no catches for this competition.');
  } else if (!catches.some(isReviewed)) {
    warnings.push('No catch has been checked by a person.');
  }

  return { ready: blockers.length === 0, blockers, warnings };
}

/** Rotation for a catch photo: 0, 90, 180 or 270 degrees clockwise. */
export const VALID_ROTATIONS = [0, 90, 180, 270] as const;

export function normaliseRotation(value: unknown): 0 | 90 | 180 | 270 {
  const n = Number(value);
  return (VALID_ROTATIONS as readonly number[]).includes(n) ? (n as 0 | 90 | 180 | 270) : 0;
}

export function nextRotation(value: unknown): 0 | 90 | 180 | 270 {
  return ((normaliseRotation(value) + 90) % 360) as 0 | 90 | 180 | 270;
}
